import { call, put, select, takeLatest } from "redux-saga/effects";
import {
  loginRequest,
  loginSuccess,
  loginFailure,
  registerRequest,
  registerSuccess,
  registerFailure,
  checkAuthRequest,
  checkAuthSuccess,
  checkAuthFailure,
  logout,
  profileRequest,
  profileSuccess,
  profileFailure,
  userStatsSuccess,
  userStatsFailure,
  userStatsRequest,
  updateProfileRequest,
  updateProfileFailure,
  updateProfileSuccess,
  reportUserRequest,
  reportUserSuccess,
  reportUserFailure,
  facebookLoginFailure,
  facebookLoginRequest,
  googleLoginFailure,
  googleLoginRequest,
} from "./authActions";

import LS from "../../utils/localStorageUtils";
import { closeSocket } from "../../services/socket";
import API from "../../utils/API";
import { isTokenExpired } from "../../utils/helpers/tokenUtils";
import { UpdateProfilePayload, UserProfile, UserStatistics } from "../types";

interface RawProfileData {
  id: number;
  stringId: string;
  email: string;
  username: string;
  sound_effects: number;
  avatar?: string;
  wallet: { chips: number };
  is_vip_user: boolean;
  level: number;
  user_purchases: { [key: string]: boolean };
  email_notifications: number;
  mobile_notifications: number;
  hand_power_meter: number;
  periodic_bonus_collected_at: string;
  periodic_bonus_hours: string;
  vip_expired_at: string;
}
import {
  createPlayerReport,
  getUserProfile,
  getUserStatistics,
  refreshTokenRequest,
  socialLoginFacebook,
  socialLoginGoogle,
  updateProfile,
} from "../../services/api";
import Toast from "react-native-toast-message";
import i18next from "i18next";
import { toast } from "../../utils/helpers/toastUtils";
import { RootState } from "..";
import { analyticsEvents } from "../../services/firebase/analytics-events.service";
import { cleverTapService } from "../../services/clevertap/clevertap.service";
import { deltaDNAService } from "../../services/deltaDNA/deltadna.service";
import { facebookSignIn } from "../../services/socialLogins/facebookSignIn";
import googleSignIn from "../../services/socialLogins/googleSignIn";
import { env } from "../../utils/env";
import { miniGamesRewardsRequest } from "../jackpot/miniGamesRewardsActions";

function mapProfile(player: RawProfileData): UserProfile {
  const vipExpiredAt =
    (player as any).vip_expired_at ?? (player as any).vipExpiredAt ?? null;
  return {
    id: player.stringId,
    numericId: player.id,
    username: player.username,
    email: player.email,
    isSoundEffectsEnabled: !!player.sound_effects,
    avatarFileId: player.avatar || "",
    chips: player.wallet.chips || 0,
    isVip: player.is_vip_user,
    level: player.level,
    user_purchases: player.user_purchases,

    isEmailEnabled: !!player.email_notifications,
    isMobileEnabled: !!player.mobile_notifications,
    isHandPowerEnabled: !!player.hand_power_meter,
    collectedAt: player.periodic_bonus_collected_at,
    bonusHours: player.periodic_bonus_hours,
    vipExpiredAt,
  };
}

function* loginSaga(action: ReturnType<typeof loginRequest>) {
  try {
    const response = yield call(
      [API, API.authRequest],
      "/login",
      "post",
      action.payload,
      { "Content-Type": "application/json", ...API.getXCasinoHeader() },
    );

    if (response.Error) throw response.Error;

    const {
      access_token,
      refresh_token,
      user,
    }: { access_token: string; refresh_token?: string; user: any } =
      response.Data;

    LS.set("access_token", access_token);
    if (refresh_token) {
      LS.set("refresh_token", refresh_token);
    }

    yield put(loginSuccess({ token: access_token, user }));
    toast.success(
      "auth.login_success_title",
      "Login successful",
      "Welcome back!",
    );
  } catch (err: any) {
    yield put(loginFailure(err.message || "Login failed"));
    toast.error("auth.login_failed_title", "Login failed", err.message);
  }
}

function* registerWorker(action: ReturnType<typeof registerRequest>) {
  try {
    const { Data, Error } = yield call(
      [API, API.authRequest],
      "/signup",
      "post",
      action.payload,
    );

    if (Error) {
      const msg =
        Error?.response?.data?.message ||
        Error.message ||
        "Registration failed";
      yield put(registerFailure(msg));
      return;
    }

    yield put(registerSuccess());

    yield put(
      loginRequest({
        username: action.payload.username,
        password: action.payload.password,
      }),
    );
  } catch (e: any) {
    yield put(registerFailure(e?.message || "Registration failed"));
  }
}

function* refreshAccessTokenSaga() {
  const storedRefreshToken = LS.get("refresh_token");

  if (!storedRefreshToken) {
    throw new Error("No refresh token found");
  }

  const data: {
    access_token: string;
    refresh_token?: string;
    id?: number;
  } = yield call(refreshTokenRequest, storedRefreshToken);

  const newAccessToken = data?.access_token;
  const newRefreshToken = data?.refresh_token;

  if (!newAccessToken) {
    throw new Error("No access token received from refresh endpoint");
  }

  LS.set("access_token", newAccessToken);

  if (newRefreshToken) {
    LS.set("refresh_token", newRefreshToken);
  }

  const storedUser = LS.get("auth_user");
  if (storedUser) {
    try {
      const parsed = JSON.parse(storedUser);

      const updatedUser = {
        ...parsed,
        accessToken: newAccessToken,
        refreshToken: newRefreshToken ?? parsed.refreshToken,
      };

      LS.set("auth_user", JSON.stringify(updatedUser));
    } catch {
      // ignore parse errors
    }
  }

  return newAccessToken;
}

function* checkAuthWorker() {
  try {
    const token = LS.get("access_token");

    if (!token) {
      yield put(checkAuthFailure());
      yield put(logout());
      return;
    }

    if (isTokenExpired(token)) {
      try {
        const newAccessToken: string = yield call(refreshAccessTokenSaga);
        yield put(checkAuthSuccess({ token: newAccessToken }));
        yield put(profileRequest());
        return;
      } catch (refreshError) {
        analyticsEvents.trackUserLogout();
        yield call([cleverTapService, "trackEvent"], "User Logout", {
          reason: "refresh_failed",
          platform: "web",
        });
        deltaDNAService.reset();

        yield put(checkAuthFailure());
        yield put(logout());
        return;
      }
    }

    yield put(checkAuthSuccess({ token }));
    yield put(profileRequest());
  } catch {
    yield put(checkAuthFailure());
    yield put(logout());
  }
}

function* fetchUserStats() {
  try {
    const raw = yield call(getUserStatistics);
    const stats: UserStatistics = mapStatistics(raw);
    yield put(userStatsSuccess(stats));
  } catch (e: any) {
    yield put(userStatsFailure(e?.message || "Failed to load user statistics"));
  }
}

function* afterLoginSaga() {
  try {
    console.log("[Auth] afterLoginSaga START");
    const profileRaw = yield call(getUserProfile);
    console.log("[Auth] profileRaw:", profileRaw ? "OK" : "EMPTY", profileRaw);
    if (!profileRaw) {
      yield put(profileFailure("No profile data received"));
      return;
    }

    const user: UserProfile = mapProfile(profileRaw);

    const accessToken = LS.get("access_token") || undefined;
    const refreshToken = LS.get("refresh_token") || undefined;

    const enrichedUser: UserProfile = {
      ...user,
      ...(accessToken ? { accessToken } : {}),
      ...(refreshToken ? { refreshToken } : {}),
    };

    LS.set("auth_user", JSON.stringify(enrichedUser));
    yield put(profileSuccess(enrichedUser));

    yield put(miniGamesRewardsRequest());

    analyticsEvents.trackUserLogin();
    yield call([cleverTapService, "setUserProfile"], {
      Identity: user.id || user.numericId.toString(),
      Name: user.username,
      Email: user.email,
      player_level: user.level || 1,
      is_vip: user.isVip || false,
      total_chips: user.chips || 0,
      avatar: user.avatarFileId || "",
      is_sound_enabled: user.isSoundEffectsEnabled || false,
      is_hand_power_enabled: user.isHandPowerEnabled || false,
    });

    yield call([cleverTapService, "trackEvent"], "User Login", {
      method: "email",
      platform: "web",
      user_id: user.id,
      username: user.username,
      is_vip: user.isVip,
      level: user.level,
    });
    yield call(
      [deltaDNAService, "initialize"],
      user.id || user.numericId.toString(),
    );
    console.log("[Auth] ✅ User profile and analytics tracking complete");

    console.log("[Auth] mapped user", { id: user.id, username: user.username });
    console.log("[Auth] profileSuccess.type", profileSuccess.type);
    yield put(userStatsRequest());

    yield call(fetchUserStats);
  } catch (err: any) {
    console.log("[Auth] afterLoginSaga ERROR", err?.message, err);
    yield put(profileFailure(err.message || "Profile failed"));
    yield put(
      userStatsFailure(err?.message || "Failed to load user statistics"),
    );
  }
}

function* profileWorker() {
  try {
    const profileRaw = yield call(getUserProfile);
    if (!profileRaw) {
      yield put(profileFailure("No profile data received"));
      return;
    }

    const user: UserProfile = mapProfile(profileRaw);

    // Preserve existing tokens if present in stored profile
    let existingTokens: Pick<UserProfile, "accessToken" | "refreshToken"> = {};
    const stored = LS.get("auth_user");
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as UserProfile;
        existingTokens = {
          accessToken: parsed.accessToken,
          refreshToken: parsed.refreshToken,
        };
      } catch {
        existingTokens = {};
      }
    }

    const enrichedUser: UserProfile = {
      ...user,
      ...existingTokens,
    };

    LS.set("auth_user", JSON.stringify(enrichedUser));
    yield put(profileSuccess(enrichedUser));
  } catch (err: any) {
    yield put(profileFailure(err.message || "Profile failed"));
  }
}

function* updateProfileWorker(action: ReturnType<typeof updateProfileRequest>) {
  try {
    const updated: UserProfile = yield call(updateProfile, action.payload);

    const finalProfile: RawProfileData = yield call(getUserProfile);
    const user: UserProfile = mapProfile(finalProfile);

    // Preserve existing tokens if present in stored profile
    let existingTokens: Pick<UserProfile, "accessToken" | "refreshToken"> = {};
    const stored = LS.get("auth_user");
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as UserProfile;
        existingTokens = {
          accessToken: parsed.accessToken,
          refreshToken: parsed.refreshToken,
        };
      } catch {
        existingTokens = {};
      }
    }

    const enrichedUser: UserProfile = {
      ...user,
      ...existingTokens,
    };

    LS.set("auth_user", JSON.stringify(enrichedUser));
    yield put(profileSuccess(enrichedUser));

    yield put(updateProfileSuccess());
  } catch (e: any) {
    const msg =
      e?.response?.data?.message || e?.message || "Update profile failed";
    yield put(updateProfileFailure(msg));
  }
}

function* reportUserWorker(action: ReturnType<typeof reportUserRequest>) {
  try {
    const reporter = yield select((state: RootState) => state.auth.user);

    if (!reporter) throw new Error("You must be logged in to report a player");

    const body = {
      reporterId: reporter.id,
      reportedId: action.payload.reportedId,
      reportType: action.payload.reportType,
      reason: action.payload.reason || "",
      gameContext: action.payload.gameContext || "PROFILE",
    };

    yield call(createPlayerReport, body);

    yield put(reportUserSuccess());

    toast.success(
      "report.success_title",
      "Report sent",
      "Thank you — the team will review this player.",
    );
  } catch (e: any) {
    const msg = e?.response?.data?.message || e.message || "Report failed";
    yield put(reportUserFailure(msg));
    toast.error("report.failed_title", "Report failed", msg);
  }
}

function* googleLoginSaga() {
  try {
    console.log("🔵 [Google Login Saga] Starting Google authentication");
    console.log(
      "🔵 [Google Login Saga] googleSignIn type:",
      typeof googleSignIn,
    );

    // Call the function - ensure it's defined
    if (typeof googleSignIn !== "function") {
      console.error("❌ [Google Login Saga] googleSignIn is not a function");
      throw new Error("Google Sign-In is not a function");
    }

    console.log("🔵 [Google Login Saga] Calling googleSignIn()...");
    const idToken: string = yield call(googleSignIn);
    console.log(
      "🔵 [Google Login Saga] Received idToken:",
      idToken ? `${idToken.substring(0, 20)}...` : "null/undefined",
    );

    if (!idToken) {
      console.error("❌ [Google Login Saga] No Google idToken received");
      throw new Error("No Google idToken received");
    }

    console.log("🔵 [Google Login Saga] Calling socialLoginGoogle API...");
    const data: any = yield call(socialLoginGoogle, idToken);
    console.log(
      "🔵 [Google Login Saga] API response:",
      data
        ? { hasAccessToken: !!data.access_token, hasUser: !!data.user }
        : "null",
    );

    const accessToken = data?.access_token || data?.accessToken || data?.token;
    const refreshToken =
      data?.refresh_token || data?.refreshToken || data?.refresh;
    console.log(
      "🔵 [Google Login Saga] Extracted accessToken:",
      accessToken ? `${accessToken.substring(0, 20)}...` : "null/undefined",
    );

    if (!accessToken) {
      console.error(
        "❌ [Google Login Saga] No access token received from backend",
      );
      console.error(
        "❌ [Google Login Saga] Full response:",
        JSON.stringify(data, null, 2),
      );
      throw new Error("No access token received from backend");
    }

    console.log(
      "🔵 [Google Login Saga] Saving access token to localStorage...",
    );
    LS.set("access_token", accessToken);
    if (refreshToken) {
      LS.set("refresh_token", refreshToken);
    }

    console.log("🔵 [Google Login Saga] Dispatching loginSuccess...");

    yield put(loginSuccess({ token: accessToken, user: data?.user }));

    console.log("✅ [Google Login Saga] Login successful!");
    toast.success(
      "auth.login_success_title",
      "Login successful",
      "Welcome back!",
    );
  } catch (err: any) {
    const msg = err?.message || "Google login failed";
    console.error("❌ [Google Login Saga] Login failed");
    console.error("❌ [Google Login Saga] Error message:", msg);
    console.error("❌ [Google Login Saga] Error object:", err);
    console.error("❌ [Google Login Saga] Error stack:", err?.stack);
    console.error(
      "❌ [Google Login Saga] Full error:",
      JSON.stringify(err, Object.getOwnPropertyNames(err), 2),
    );

    yield put(googleLoginFailure(msg));
    toast.error("auth.login_failed_title", "Google login failed", msg);
  }
}

// function* facebookLoginSaga() {
//   try {
//     console.log("[Facebook Login] Starting Facebook authentication");

//     const fbAccessToken: string = yield call(facebookSignIn);
//     if (!fbAccessToken) throw new Error("No Facebook access token received");

//     console.log("[Facebook Login] Token obtained, sending to backend");

//     const response = yield call(
//       [API, API.authRequest],
//       "/socialLogin",
//       "post",
//       { accessToken: fbAccessToken, socialPlatform: "Facebook" },
//       { "Content-Type": "application/json", ...API.getXCasinoHeader() },
//     );

//     if (response.Error) {
//       const errorMessage =
//         response.Error?.response?.data?.message ||
//         response.Error?.message ||
//         response.Error?.error ||
//         "Facebook login failed";
//       throw new Error(errorMessage);
//     }

//     const data = response.Data?.data || response.Data;
//     const { access_token, user } = data;

//     if (!access_token) throw new Error("No access token received from backend");

//     LS.set("access_token", access_token);

//     yield put(facebookLoginSuccess({ token: access_token, user }));
//     yield put(loginSuccess({ token: access_token, user })); // за да ползва afterLoginSaga
//     toast.success(
//       "auth.login_success_title",
//       "Login successful",
//       "Welcome back!",
//     );
//   } catch (err: any) {
//     const msg = err?.message || "Facebook login failed";
//     console.error("[Facebook Login] Login failed:", msg, err);
//     yield put(facebookLoginFailure(msg));
//     toast.error("auth.login_failed_title", "Facebook login failed", msg);
//   }
// }

function* facebookLoginSaga() {
  const traceId = `fb_${Date.now()}`;

  try {
    const fbAccessToken: string = yield call(facebookSignIn);

    if (!fbAccessToken) {
      throw new Error("No Facebook access token received");
    }

    const data: any = yield call(socialLoginFacebook, fbAccessToken);

    console.log(`🟢 [FB][${traceId}] backend response`, {
      hasData: !!data,
      keys: data ? Object.keys(data) : null,
      raw: data,
    });

    const accessToken = data?.access_token || data?.accessToken || data?.token;
    const refreshToken =
      data?.refresh_token || data?.refreshToken || data?.refresh;

    if (!accessToken) {
      throw new Error("No access token received from backend");
    }

    LS.set("access_token", accessToken);
    if (refreshToken) {
      LS.set("refresh_token", refreshToken);
    }

    yield put(loginSuccess({ token: accessToken, user: data?.user }));

    toast.success(
      "auth.login_success_title",
      "Login successful",
      "Welcome back!",
    );
  } catch (err: any) {
    const msg =
      err?.response?.data?.message || err?.message || "Facebook login failed";

    yield put(facebookLoginFailure(msg));

    toast.error("auth.login_failed_title", "Facebook login failed", msg);
  }
}

function* logoutCloseSocketWorker() {
  yield call(closeSocket);
}

export function* authRootSaga() {
  yield takeLatest(logout.type, logoutCloseSocketWorker);
  yield takeLatest(loginRequest.type, loginSaga);
  yield takeLatest(registerRequest.type, registerWorker);
  yield takeLatest(checkAuthRequest.type, checkAuthWorker);
  yield takeLatest(loginSuccess.type, afterLoginSaga);
  yield takeLatest(userStatsRequest.type, fetchUserStats);
  yield takeLatest(profileRequest.type, profileWorker);
  yield takeLatest(checkAuthSuccess.type, function* () {
    yield put(userStatsRequest());
  });

  yield takeLatest(updateProfileRequest.type, updateProfileWorker);
  yield takeLatest(reportUserRequest.type, reportUserWorker);
  yield takeLatest(facebookLoginRequest.type, facebookLoginSaga);
  yield takeLatest(googleLoginRequest.type, googleLoginSaga);
}

function mapStatistics(d: any): UserStatistics {
  const player = d?.data?.player ?? {};
  // Support both snake_case (vip_expired_at) and camelCase (vipExpiredAt) from API
  const vipExpiredAt = player.vip_expired_at ?? player.vipExpiredAt ?? null;

  return {
    player: {
      avatar: player.avatar ?? {},
      username: player.username ?? "",
      registrationDate: player.registrationDate,
      lastLoginDate: player.lastLoginDate,
      vip_expired_at: vipExpiredAt,
    },
    level: {
      current: Number(d?.data.level?.current ?? 0),
      name: String(d?.data.level?.name ?? ""),
    },
    gamesData: {
      svarka_wins: Number(d?.data.gamesData?.svarka_wins ?? 0),
      svarka_loses: Number(d?.data.gamesData?.svarka_loses ?? 0),
      svarka_chips_wins: String(d?.data.gamesData?.svarka_chips_wins ?? "0"),
      svarka_time_played: Number(d?.data.gamesData?.svarka_time_played ?? 0),
      svarka_last_time_played:
        d?.data.gamesData?.svarka_last_time_played ?? null,
      svarka_level: Number(d?.data.gamesData?.svarka_level ?? 0),
      svarka_minimal_experience: Number(
        d?.data.gamesData?.svarka_minimal_experience ?? 0,
      ),
      svarka_maximal_experience: Number(
        d?.data.gamesData?.svarka_maximal_experience ?? 0,
      ),
    },
    statistics: {
      svarka_time_played: String(d?.data.statistics?.timePlayed ?? "0"),
      winLoseRatio: Number(d?.data.statistics?.winLoseRatio ?? 0),
      totalChipsGained: d?.data.statistics?.totalChipsGained ?? 0,
    },
  };
}
